/**
 * Home/Dashboard Page
 * Main landing page after login
 * Matches the Commvault Customer Support Center dashboard design
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LoginButton } from '../components/LoginButton';
import { profileApiClient } from '../services/profileApiClient';
import { useSupport } from '../context/SupportContext';
import { Loading } from '../components/common';
import PageLayout from '../components/PageLayout';
import {
  SLAPanel,
  HealthPanel,
  ProfileInfoPanel,
  SecurityPosturePanel,
  EntitlementsPanel,
  CommunicationPreferencesPanel,
  ProductAnnouncementsPanel,
  SupportCasesPanel,
  AccountTeamPanel,
} from '../components/dashboard';
import styles from './HomePage.module.css';

interface DashboardData {
  user?: {
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    fullName: string;
    phone?: string;
    mobilePhone?: string;
    address?: string;
    role?: string;
  };
  sla?: {
    met: number;
    metCount: number;
    missedCount: number;
  };
  health?: {
    totalResources: number;
    critical: number;
    warning: number;
    neutral: number;
    good: number;
  };
  securityPosture?: {
    secured: number;
    warnings: number;
    good: number;
  };
  entitlements?: Array<{
    name: string;
    status: string;
    expiryDate: string;
    used: number;
    total: number;
    unit: string;
    monthsLeft?: number | null;
  }>;
  communicationPreferences?: {
    supportCommunications: boolean;
    productAnnouncements: boolean;
    technicalDocumentation: boolean;
    webinarsAndEvents: boolean;
    trainingAndLearning: boolean;
    commvaultNews: boolean;
    whitePapers: boolean;
    communityActivity: boolean;
  };
  productAnnouncements?: Array<{
    id: number;
    title: string;
    description: string;
    date: string;
    views: number;
    likes: number;
  }>;
  accountTeam?: Array<{
    name: string;
    role: string;
    email: string;
    phone: string;
  }>;
}

// Fallback data when API is unavailable
const getFallbackData = (): DashboardData => ({
  sla: {
    met: 95,
    metCount: 19,
    missedCount: 1
  },
  health: {
    totalResources: 150,
    critical: 2,
    warning: 8,
    neutral: 15,
    good: 125
  },
  securityPosture: {
    secured: 85,
    warnings: 10,
    good: 90
  },
  entitlements: [
    {
      name: 'Backup Storage',
      status: 'Active',
      expiryDate: '2026-12-31',
      used: 2500,
      total: 5000,
      unit: 'GB',
      monthsLeft: 12
    },
    {
      name: 'Virtual Machines',
      status: 'Active',
      expiryDate: '2026-06-30',
      used: 45,
      total: 100,
      unit: 'VMs',
      monthsLeft: 6
    }
  ],
  communicationPreferences: {
    supportCommunications: true,
    productAnnouncements: true,
    technicalDocumentation: false,
    webinarsAndEvents: true,
    trainingAndLearning: false,
    commvaultNews: true,
    whitePapers: false,
    communityActivity: true
  },
  productAnnouncements: [
    {
      id: 1,
      title: 'TIME + Commvault CISO of the Year Nominations: Recognize a Visionary Security Exec',
      description: 'Do you work with a visionary CISO or senior security exec who is blazing trails, operationalizing resilience and readiness, and delivering business impact?...',
      date: '17 hours ago',
      views: 7,
      likes: 0
    }
  ]
});

const HomePage: React.FC = () => {
  const { user: authUser, isAuthenticated } = useAuth();
  const { metrics: supportMetrics, accountTeam: globalAccountTeam } = useSupport();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Check if this is an SSO callback (has code parameter from IdP)
  useEffect(() => {

  }, [searchParams, navigate]);

  useEffect(() => {
    const loadData = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Fetch user profile from Dynamics CRM using profileApiClient
        try {
          console.log('[HomePage] Fetching user profile from API...');
          const profileData = await profileApiClient.getProfile();
          console.log('[HomePage] User profile received:', JSON.stringify(profileData, null, 2));
          setUserProfile(profileData);
        } catch (profileError) {
          console.error('[HomePage] Profile API failed:', profileError);
        }

        // Fetch real dashboard data from API
        try {
          console.log('[HomePage] Fetching dashboard data from API...');
          const dashboardRes = await profileApiClient.getDashboard();
          console.log('[HomePage] Dashboard data received:', dashboardRes);
          setDashboardData(dashboardRes);
        } catch (dashboardError) {
          console.error('[HomePage] Dashboard API failed, using fallback:', dashboardError);
          setDashboardData(getFallbackData());
        }

        setLoading(false);
        setError(null);
      } catch (err) {
        console.warn('[HomePage] Error loading data:', err);
        setDashboardData(getFallbackData());
        setLoading(false);
        setError(null);
      }
    };

    loadData();
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <div className={styles.container}>
        <div className={styles.loginContainer}>
          <h1>Welcome to Customer Portal</h1>
          <p>Please login to access the Customer Portal</p>
          <LoginButton />
          <div className={styles.infoBox}>
            <h3>Getting Started</h3>
            <ul>
              <li>Click the "Login with SSO" button above</li>
              <li>You will be redirected to the Commvault SSO page</li>
              <li>Enter your credentials</li>
              <li>After authentication, you'll be redirected back to the portal</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.container}>
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <h2>Error Loading Dashboard</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const user = userProfile ? {
    firstName: userProfile.firstName || 'User',
    lastName: userProfile.lastName || '',
    email: userProfile.email || authUser?.email || '',
    fullName: userProfile.fullName || '',
    phone: userProfile.phone || userProfile.telephone || undefined,
    mobilePhone: userProfile.mobilePhone || undefined,
    address: userProfile.address || undefined,
    role: userProfile.role || userProfile.jobtitle || undefined,
    company: userProfile.company || undefined,
  } : (dashboardData?.user || {
    firstName: authUser?.firstName || 'User',
    lastName: authUser?.lastName || '',
    email: authUser?.email || '',
    phone: undefined,
    mobilePhone: undefined,
    address: undefined,
    role: undefined,
  });

  return (
    <PageLayout
      userProfile={userProfile}
      showSidebar={false}
    >
      <div className={styles.dashboardLayout}>
        {/* Left Column: Core Metrics and Announcements */}
        <div className={styles.mainColumn}>
          {/* Metrics Row 1 */}
          <div className={styles.metricsRow}>
            {dashboardData?.sla && (
              <SLAPanel
                met={dashboardData.sla.met}
                metCount={dashboardData.sla.metCount}
                missedCount={dashboardData.sla.missedCount}
              />
            )}
            {dashboardData?.health && (
              <HealthPanel
                totalResources={dashboardData.health.totalResources}
                critical={dashboardData.health.critical}
                warning={dashboardData.health.warning}
                neutral={dashboardData.health.neutral}
                good={dashboardData.health.good}
              />
            )}
          </div>

          {/* Metrics Row 2 */}
          <div className={styles.metricsRow}>
            {dashboardData?.securityPosture && (
              <SecurityPosturePanel
                secured={dashboardData.securityPosture.secured}
                warnings={dashboardData.securityPosture.warnings}
                good={dashboardData.securityPosture.good}
              />
            )}
            {dashboardData?.entitlements && (
              <EntitlementsPanel entitlements={dashboardData.entitlements} />
            )}
          </div>

          {/* Product Announcements - Spans Main Column */}
          {dashboardData?.productAnnouncements && (
            <div className={styles.announcementsWrapper}>
              <ProductAnnouncementsPanel
                announcements={dashboardData.productAnnouncements}
              />
            </div>
          )}
        </div>

        {/* Right Column: Combined Profile, Support, and Team */}
        <div className={styles.sideColumn}>
          <ProfileInfoPanel
            firstName={user.firstName}
            lastName={user.lastName}
            email={user.email}
            phone={user.phone}
            mobilePhone={user.mobilePhone}
            address={user.address}
            role={user.role}
            preferences={dashboardData?.communicationPreferences || undefined}
          />

          <SupportCasesPanel
            needsAttention={supportMetrics.needsAttention}
            inProgress={supportMetrics.inProgress}
            notStarted={supportMetrics.notStarted}
          />

          <AccountTeamPanel teamMembers={globalAccountTeam} />
        </div>
      </div>
    </PageLayout>
  );
};

export default HomePage;



